<template>
  <div class="expense-sheet-card-list">
    <Card
      v-for="(item, index) in items"
      :key="item.id"
      class="expense-sheet-card"
      :class="{
        'card-first': index === 0 && items.length > 1,
        'card-middle': index > 0 && index < items.length - 1,
        'card-last': index === items.length - 1 && items.length > 1,
      }"
      @click="$emit('show-detail', item)"
    >
      <template #content>
        <div class="card-content">
          <div class="card-left">
            <div class="card-name">{{ item.name }}</div>
            <div class="card-date">{{ formatOnlyDate(item.creationDate) }}</div>
          </div>
          <div class="card-right">
            <div class="mr-2">
              <div class="card-amount">
                {{ formatCurrency(item.amount) }}
              </div>
              <div
                class="card-status"
                :style="{ color: getStatusColor(Number(item.status)) }"
                :class="{ 'p-tag-forthcoming': Number(item.status) === -1 }"
              >
                {{ getStatusText(Number(item.status)) }}
              </div>
            </div>
            <i class="pi pi-chevron-right card-icon"></i>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { ExpenseSheet } from "@wkt/api/expense-sheets";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import { useExpenseSheets } from "@wkt/composables/useExpenseSheets";
import { useUtils } from "@wkt/composables/useUtils";

// Props
interface Props {
  items: ExpenseSheet[];
}

defineProps<Props>();

// Emits
defineEmits<{
  "show-detail": [item: ExpenseSheet];
}>();

// Composables
const { formatOnlyDate } = useDateFormatter();
const { getStatusText, getStatusColor } = useExpenseSheets();
const { formatCurrency } = useUtils()
</script>

<style scoped>
.expense-sheet-card-list {
  padding-bottom: 5rem;
  width: 100%;
}

.expense-sheet-card {
  cursor: pointer;
  margin-bottom: 0;
  border-radius: 0.375rem;
  border: 1px solid var(--border-color);
  box-shadow: none;
  transition: all 0.2s ease;
}

.expense-sheet-card:hover {
  background: var(--surface-50);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.expense-sheet-card:not(:last-child) {
  margin-bottom: -1px;
}

.card-first {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.card-middle {
  border-radius: 0;
}

.card-last {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.expense-sheet-card :deep(.p-card-body) {
  padding: 0;
}

.expense-sheet-card :deep(.p-card-content) {
  padding: 0;
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
}

.card-left {
  flex: 1;
  min-width: 0;
}

.card-name {
  color: #424242;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 0.25rem;
}

.card-date {
  color: #757575;
  font-size: 13px;
}

.card-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: right;
}

.card-amount {
  color: #000;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.card-status {
  font-size: 13px;
  white-space: nowrap;
}

.card-status.p-tag-forthcoming {
  color: #424242 !important;
  font-weight: 400;
}

.card-icon {
  color: var(--text-color-secondary);
  font-size: 1.25rem;
}

@media (max-width: 768px) {
  .expense-sheet-card-list {
    border-radius: 0.375rem;
    padding-bottom: 0;
  }
  .expense-sheet-card.p-card .p-card-body .p-card-content .card-content {
    border-bottom: 1px solid var(--border-color) !important;
  }
  .expense-sheet-card.p-card.card-last .p-card-body .p-card-content .card-content {
    border-bottom: none !important;
  }
}
</style>
